import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { ENQUIRY_STATUS, ENQUIRY_STATUS_COLORS } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, X, Mail, Phone, Trash2, Inbox, AlertCircle, UserPlus, CheckCircle2 } from "lucide-react";

const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

const SOURCE_BADGES = {
  "cash-offer-form": ["Cash Offer", "bg-amber-50 text-amber-700 border-amber-200"],
  "investor-form": ["Investor", "bg-blue-50 text-blue-700 border-blue-200"],
  "landing-popup": ["Contact", "bg-slate-50 text-slate-600 border-slate-200"],
};

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [active, setActive] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [converting, setConverting] = useState(false);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/contact-submissions")
      .then((r) => setItems(r.data || []))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); else toast.error(formatApiError(e?.response?.data?.detail)); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (item, status) => {
    const prev = items;
    setItems((arr) => arr.map((x) => x.id === item.id ? { ...x, status } : x));
    try {
      await api.patch(`/contact-submissions/${item.id}`, { status });
      toast.success(`Marked as ${status}`);
    } catch (e) { setItems(prev); toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const del = async () => {
    const item = confirmDel;
    setConfirmDel(null);
    try {
      await api.delete(`/contact-submissions/${item.id}`);
      setItems((arr) => arr.filter((x) => x.id !== item.id));
      toast.success("Enquiry deleted");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const convert = async (item) => {
    if (item.converted_lead_id) { nav(`/leads/${item.converted_lead_id}`); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/contact-submissions/${item.id}/convert`);
      toast.success(`Lead ${data.lead_id_display || ""} created`);
      setItems((arr) => arr.map((x) => x.id === item.id ? { ...x, status: "Closed", converted_lead_id: data.lead_id } : x));
      nav(`/leads/${data.lead_id}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not convert to lead");
    } finally { setConverting(false); }
  };

  const filtered = items.filter((it) => {
    const okStatus = statusFilter === "all" || (it.status || "New") === statusFilter;
    const haystack = [it.name, it.email, it.phone, it.interest, it.message, it.source].filter(Boolean).join(" ").toLowerCase();
    const query = q.trim().toLowerCase();
    return okStatus && (!query || haystack.includes(query));
  });

  const newCount = items.filter((i) => (i.status || "New") === "New").length;

  if (denied) {
    return (
      <div>
        <PageHeader title="Enquiries" />
        <div className="p-8">
          <div className="flex items-center gap-3 rounded-md border border-border bg-card p-6 text-muted-foreground" data-testid="enquiries-denied">
            <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
            You don't have permission to view landing-page enquiries.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="enquiries-page">
      <PageHeader title="Enquiries" subtitle={loading ? "Loading…" : `${items.length} total · ${newCount} new`} />
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, message…" className="pl-9 pr-9" data-testid="enquiry-search" />
            {q && <button onClick={() => setQ("")} data-testid="enquiry-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="enquiry-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {ENQUIRY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-card border border-border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead>Contact</TableHead>
                <TableHead className="hidden md:table-cell">Interest</TableHead>
                <TableHead className="hidden lg:table-cell">Message</TableHead>
                <TableHead className="hidden sm:table-cell">Received</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => (
                <TableRow key={it.id} className="hover:bg-secondary/60 transition-colors cursor-pointer" onClick={() => setActive(it)} data-testid={`enquiry-row-${it.id}`}>
                  <TableCell className="font-medium">
                    {it.name || <span className="text-muted-foreground">No name</span>}
                    {it.converted_lead_id && <Badge variant="outline" className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] py-0">Converted</Badge>}
                    <span className="block text-xs font-normal text-muted-foreground truncate max-w-[220px]">{it.email}</span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground max-w-[240px]">
                    {SOURCE_BADGES[it.source] && <Badge variant="outline" className={`${SOURCE_BADGES[it.source][1]} text-[10px] py-0 mr-1.5`}>{SOURCE_BADGES[it.source][0]}</Badge>}
                    <span className="truncate">{it.interest || "—"}</span>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground max-w-[280px] truncate">{it.message || "—"}</TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground whitespace-nowrap">{fmtDateTime(it.created_at)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select value={it.status || "New"} onValueChange={(v) => setStatus(it, v)}>
                      <SelectTrigger className="h-8 w-32" data-testid={`enquiry-status-${it.id}`}>
                        <Badge variant="outline" className={ENQUIRY_STATUS_COLORS[it.status || "New"]}>{it.status || "New"}</Badge>
                      </SelectTrigger>
                      <SelectContent>{ENQUIRY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setConfirmDel(it)} data-testid={`enquiry-delete-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-14">
                  <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No enquiries {q.trim() || statusFilter !== "all" ? "match your filters" : "yet"}.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg" data-testid="enquiry-detail">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  {active.name || "Enquiry"}
                  <Badge variant="outline" className={ENQUIRY_STATUS_COLORS[active.status || "New"]}>{active.status || "New"}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-accent" /><a href={`mailto:${active.email}`} className="text-primary hover:underline" data-testid="enquiry-detail-email">{active.email}</a></div>
                {active.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-accent" /><a href={`tel:${active.phone}`} className="text-primary hover:underline">{active.phone}</a></div>}
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Interest</div><div>{active.interest || "—"}</div></div>
                {active.details && Object.keys(active.details).length > 0 && (
                  <div data-testid="enquiry-details">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Form Details</div>
                    <div className="mt-1 space-y-1">
                      {Object.entries(active.details).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-sm"><span className="text-muted-foreground capitalize min-w-[110px]">{k.replace(/_/g, " ")}:</span><span>{v}</span></div>
                      ))}
                    </div>
                  </div>
                )}
                <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Message</div><div className="whitespace-pre-wrap rounded-md bg-secondary/50 p-3 border border-border">{active.message || "—"}</div></div>
                <div className="flex justify-between text-xs text-muted-foreground pt-1">
                  <span>Source: {active.source || "—"}</span>
                  <span>{fmtDateTime(active.created_at)}</span>
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Select value={active.status || "New"} onValueChange={(v) => { setStatus(active, v); setActive({ ...active, status: v }); }}>
                  <SelectTrigger className="w-32" data-testid="enquiry-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENQUIRY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                {active.converted_lead_id ? (
                  <Button variant="outline" onClick={() => convert(active)} data-testid="enquiry-view-lead">
                    <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" /> View Lead
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => convert(active)} disabled={converting} data-testid="enquiry-convert-lead">
                    <UserPlus className="w-4 h-4 mr-1" /> {converting ? "Converting…" : "Convert to Lead"}
                  </Button>
                )}
                <a href={`mailto:${active.email}?subject=Re:%20Your%20enquiry%20—%20Galahadji%20Acquisitions`} data-testid="enquiry-detail-reply">
                  <Button><Mail className="w-4 h-4 mr-1" /> Reply</Button>
                </a>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-sm" data-testid="enquiry-delete-confirm">
          <DialogHeader><DialogTitle className="font-display">Delete this enquiry?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This permanently removes the enquiry from {confirmDel?.name || confirmDel?.email}. This cannot be undone.</p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setConfirmDel(null)} data-testid="enquiry-delete-cancel">Cancel</Button>
            <Button variant="destructive" onClick={del} data-testid="enquiry-delete-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
